import { ChangeEvent, useEffect, useState } from 'react';

import { Box, Divider, Grid, Typography } from '@mui/material';
import { Address, UKBankDetails } from 'types/graphql';

import { CustomInput } from 'src/components/CustomInput';
import { InputWithSearch } from 'src/components/InputWithSearch';
import Loader from 'src/components/Loader/Loader';
import AddressListModal from 'src/components/Modals/AddressListModal';
import NPIDualInput from 'src/components/NPIDualInput/NPIDualInput';
import SortCodeAccountInput from 'src/components/SortCodeAccountInput/SortCodeAccountInput';
import { useAddressLookUp } from 'src/hooks/useAddressLookup';

import { OpenModalState } from '../AddressInformation';

export type BankField = {
    name: keyof GBBankFieldsProps['values'];
    label?: string;
    placeholder?: string;
    chargeableField?: string;
    disabled?: boolean;
};

export type BankFieldsValues = {
    settlementAccountSortCode: string;
    settlementAccountAccountNumber: string;
    settlementAccountAccountName: string;
    settlementAccountBankName: string;
    settlementAccountBranch: string;
    settlementAccountAddressLine1: string;
    settlementAccountAddressLine2?: string;
    settlementAccountTown: string;
    settlementAccountPostCode: string;
    chargeableAccountSortCode: string;
    chargeableAccountAccountNumber: string;
    chargeableAccountAccountName: string;
    chargeableAccountBankName: string;
    chargeableAccountBranch: string;
    chargeableAccountAddressLine1: string;
    chargeableAccountAddressLine2?: string;
    chargeableAccountTown: string;
    chargeableAccountPostCode: string;
};

type BankFieldsErrors = {
    settlementAccountSortCode?: string;
    settlementAccountAccountNumber?: string;
    settlementAccountAccountName?: string;
    settlementAccountBankName?: string;
    settlementAccountBranch?: string;
    settlementAccountAddressLine1?: string;
    settlementAccountAddressLine2?: string;
    settlementAccountTown?: string;
    settlementAccountPostCode?: string;
    chargeableAccountSortCode?: string;
    chargeableAccountAccountNumber?: string;
    chargeableAccountAccountName?: string;
    chargeableAccountBankName?: string;
    chargeableAccountBranch?: string;
    chargeableAccountAddressLine1?: string;
    chargeableAccountAddressLine2?: string;
    chargeableAccountTown?: string;
    chargeableAccountPostCode?: string;
};

type GBBankFieldsProps = {
    heading: string;
    type: 'settlement' | 'chargeable';
    bankFields: Record<string, BankField>;
    values: BankFieldsValues;
    errors: BankFieldsErrors;
    handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
    setFieldValue: (field: string, value: string) => void;
    setValues: (values: BankFieldsValues) => void;
    setBankValidationResult: (result: UKBankDetails) => void;
    setValidatingBankDetails: (validating: boolean) => void;
    settlementAccountSameAsChargeable?: boolean;
    isEditingDisabled?: boolean;
    bankValidationResult?: UKBankDetails;
};

const GBBankFields = ({
    heading,
    type,
    bankFields,
    values,
    errors,
    handleChange,
    setFieldValue,
    setValues,
    setBankValidationResult,
    setValidatingBankDetails,
    settlementAccountSameAsChargeable,
    isEditingDisabled,
    bankValidationResult,
}: GBBankFieldsProps) => {
    const { addresses, findAddress, searchingForAddress } = useAddressLookUp();
    const [isEmptyAddressField, setIsEmptyAddressField] = useState(false);
    const [openAddressModal, setOpenAddressModal] = useState<OpenModalState>({
        open: false,
    });

    useEffect(() => {
        if (type === 'settlement') {
            setIsEmptyAddressField(
                !values.settlementAccountAddressLine1 ||
                    !values.settlementAccountTown ||
                    !values.settlementAccountPostCode
            );
        } else {
            setIsEmptyAddressField(
                !values.chargeableAccountAddressLine1 ||
                    !values.chargeableAccountTown ||
                    !values.chargeableAccountPostCode
            );
        }
    }, [
        type,
        values.settlementAccountAddressLine1,
        values.settlementAccountTown,
        values.settlementAccountPostCode,
        values.chargeableAccountAddressLine1,
        values.chargeableAccountTown,
        values.chargeableAccountPostCode,
    ]);

    const onAddressCapture = (address: string) => {
        findAddress(address);
        setOpenAddressModal({ open: true });
    };

    const onRetrievalOfAddress = (address: Address, formattedAddress: string) => {
        setFieldValue(`${type}SingleAddressSearch`, formattedAddress);
        setFieldValue(bankFields.addressLine1.name, address.line1);
        setFieldValue(bankFields.addressLine2.name, address.line2 || '');
        setFieldValue(bankFields.town.name, address.city);

        if (settlementAccountSameAsChargeable) {
            setFieldValue(bankFields.addressLine1.chargeableField, address.line1);
            setFieldValue(bankFields.addressLine2.chargeableField, address.line2 || '');
            setFieldValue(bankFields.town.chargeableField, address.city);
        }
    };

    const setFieldValues = (res: UKBankDetails) => {
        const isEmptyAddressField =
            !res.ContactAddressLine1 || !res.ContactPostTown || !res.ContactPostcode;

        setIsEmptyAddressField(isEmptyAddressField);

        setFieldValue(bankFields.bankName.name, res.Bank || '');
        setFieldValue(bankFields.branch.name, res.Branch || '');
        setFieldValue(bankFields.addressLine1.name, res.ContactAddressLine1 || '');
        setFieldValue(bankFields.addressLine2.name, res.ContactAddressLine2 || '');
        setFieldValue(bankFields.town.name, res.ContactPostTown || '');
        setFieldValue(bankFields.postCode.name, res.ContactPostcode || '');

        if (settlementAccountSameAsChargeable) {
            setFieldValue(bankFields.bankName.chargeableField, res.Bank || '');
            setFieldValue(bankFields.branch.chargeableField, res.Branch || '');
            setFieldValue(bankFields.addressLine1.chargeableField, res.ContactAddressLine1 || '');
            setFieldValue(bankFields.addressLine2.chargeableField, res.ContactAddressLine2 || '');
            setFieldValue(bankFields.town.chargeableField, res.ContactPostTown || '');
            setFieldValue(bankFields.postCode.chargeableField, res.ContactPostcode || '');
        }

        setBankValidationResult(res);
    };

    const handleAccountNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (settlementAccountSameAsChargeable) {
            // (KM) The sort code formik field value gets set inside the SortCodeAccountInput component due to special formatting
            setFieldValue(bankFields.accountNumber.chargeableField, event.target.value);
        }
        handleChange(event);
    };

    const shouldEnableField = (fieldKey: string, validationField: keyof UKBankDetails) => {
        const field = bankFields[fieldKey];
        if (isEditingDisabled || field?.disabled) {
            return false;
        }

        if (!bankValidationResult) {
            return false;
        }

        const isSuccessfulValidation =
            bankValidationResult.StatusInformation === 'OK' ||
            bankValidationResult.StatusInformation === 'CautiousOK';

        // Only enable if validation was successful AND the field is empty
        if (!isSuccessfulValidation) {
            return false;
        }

        return (
            !bankValidationResult[validationField] || bankValidationResult[validationField] === ''
        );
    };

    return (
        <>
            {searchingForAddress && <Loader />}

            <Typography fontSize="24px" color="#434341" marginTop="32px" marginBottom="8px">
                {heading}
            </Typography>
            <Divider
                sx={{
                    borderColor: 'warning.main',
                    marginBottom: '32px',
                }}
            />

            <Box mb={2}>
                <SortCodeAccountInput
                    values={{
                        sortCode: values[bankFields.sortCode.name],
                        accountNumber: values[bankFields.accountNumber.name],
                    }}
                    customFieldNames={{
                        sortCode: bankFields.sortCode.name,
                        additionalSortCode: bankFields.sortCode.chargeableField,
                        accountNumber: bankFields.accountNumber.name,
                    }}
                    setFieldValue={(_field: string, value: string) => {
                        const updatedValues = { ...values };
                        updatedValues[bankFields.sortCode.name] = value;
                        if (settlementAccountSameAsChargeable) {
                            updatedValues[bankFields.sortCode.chargeableField] = value;
                        }
                        // Update all values at once
                        setValues(updatedValues);
                    }}
                    handleChange={handleAccountNumberChange}
                    setBankValidationResult={setFieldValues}
                    setValidatingBankDetails={setValidatingBankDetails}
                />
            </Box>

            <Grid container rowSpacing={2} columnSpacing={2} rowGap={1}>
                <Grid item xs={12} lg={6}>
                    <CustomInput
                        name={bankFields.accountName.name}
                        label={bankFields.accountName.label}
                        onChange={(event) => {
                            if (settlementAccountSameAsChargeable) {
                                setFieldValue(
                                    bankFields.accountName.chargeableField,
                                    event.target.value
                                );
                            }
                            handleChange(event);
                        }}
                        value={values[bankFields.accountName.name]}
                        error={errors[bankFields.accountName.name]}
                        disabled
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <NPIDualInput
                        title="Bank Name / Branch"
                        component1={{
                            name: bankFields.bankName.name,
                            placeholder: bankFields.bankName.label,
                            disabled: !shouldEnableField('bankName', 'Bank'),
                            onChange: (event) => {
                                if (settlementAccountSameAsChargeable) {
                                    setFieldValue(
                                        bankFields.bankName.chargeableField,
                                        event.target.value
                                    );
                                }
                                handleChange(event);
                            },
                            inputRef: null,
                            inputProps: {},
                        }}
                        component2={{
                            name: bankFields.branch.name,
                            placeholder: bankFields.branch.label,
                            disabled: !shouldEnableField('branch', 'Branch'),
                            onChange: (event) => {
                                if (settlementAccountSameAsChargeable) {
                                    setFieldValue(
                                        bankFields.branch.chargeableField,
                                        event.target.value
                                    );
                                }
                                handleChange(event);
                            },
                            inputRef: null,
                            inputProps: {},
                        }}
                        onValidate={() => {}}
                        showValidateButton={false}
                    />
                </Grid>

                <Grid item width="100%">
                    <Grid item>
                        <InputWithSearch
                            key={isEmptyAddressField ? 'empty' : 'filled'}
                            searchText="Search"
                            onSearch={onAddressCapture}
                            placeholder="Search"
                            label="Search"
                            fieldName={`${type}SingleAddressSearch`}
                            disabled={!isEmptyAddressField}
                        />
                    </Grid>
                </Grid>
                <Grid item xs={12} lg={6}>
                    <CustomInput
                        name={bankFields.addressLine1.name}
                        label={bankFields.addressLine1.label}
                        placeholder={bankFields.addressLine1.label}
                        onChange={(event) => {
                            if (settlementAccountSameAsChargeable) {
                                setFieldValue(
                                    bankFields.addressLine1.chargeableField,
                                    event.target.value
                                );
                            }
                            handleChange(event);
                        }}
                        value={values[bankFields.addressLine1.name]}
                        error={errors[bankFields.addressLine1.name]}
                        disabled={!shouldEnableField('addressLine1', 'ContactAddressLine1')}
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <CustomInput
                        name={bankFields.addressLine2.name}
                        label={bankFields.addressLine2.label}
                        placeholder={bankFields.addressLine2.label}
                        onChange={(event) => {
                            if (settlementAccountSameAsChargeable) {
                                setFieldValue(
                                    bankFields.addressLine2.chargeableField,
                                    event.target.value
                                );
                            }
                            handleChange(event);
                        }}
                        value={values[bankFields.addressLine2.name]}
                        error={errors[bankFields.addressLine2.name]}
                        disabled={!shouldEnableField('addressLine2', 'ContactAddressLine2')}
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <CustomInput
                        name={bankFields.town.name}
                        label={bankFields.town.label}
                        placeholder={bankFields.town.label}
                        onChange={(event) => {
                            if (settlementAccountSameAsChargeable) {
                                setFieldValue(bankFields.town.chargeableField, event.target.value);
                            }
                            handleChange(event);
                        }}
                        value={values[bankFields.town.name]}
                        error={errors[bankFields.town.name]}
                        disabled={!shouldEnableField('town', 'ContactPostTown')}
                    />
                </Grid>
                <Grid item xs={12} lg={6}>
                    <CustomInput
                        name={bankFields.postCode.name}
                        label={bankFields.postCode.label}
                        placeholder={bankFields.postCode.label}
                        onChange={(event) => {
                            if (settlementAccountSameAsChargeable) {
                                setFieldValue(
                                    bankFields.postCode.chargeableField,
                                    event.target.value
                                );
                            }
                            handleChange(event);
                        }}
                        value={values[bankFields.postCode.name]}
                        error={errors[bankFields.postCode.name]}
                        disabled={!shouldEnableField('postCode', 'ContactPostcode')}
                    />
                </Grid>
            </Grid>

            <AddressListModal
                isOpen={openAddressModal.open}
                onClose={() => {
                    return setOpenAddressModal((prev) => {
                        return { ...prev, open: false };
                    });
                }}
                addressList={addresses}
                onSelectedAddress={onRetrievalOfAddress}
            />
        </>
    );
};

export default GBBankFields;
